/**
 * Слова и правила закрепления карточки §9 (1.52, §65): подпись значка,
 * тело пустой закреплённой карточки, текст отказа записи и выбор базы биржи
 * для тикера. Модуль без React — его читают и экран, и карточки.
 */

/** Подсказка и имя значка, пока список закреплений не прочитан. */
export const CARD_PIN_UNREAD_LABEL =
  "Закрепления карточек не прочитаны — нажмите, чтобы перечитать";

/** Неудача ручного перечитывания списка закреплений. */
export const CARD_PIN_RELOAD_FAILED_TEXT = "Закрепления карточек не прочитаны, попробуйте ещё раз";

/** Уведомление несёт ключ инструмента: гасить его вправе только действие того же ключа. */
export interface CardPinNotice {
  key: string;
  text: string;
}

/** Как `clearFavoriteNoticeForBase`: старт и успех по ключу X не стирают отказ по Y. */
export function clearCardPinNoticeForKey(
  previous: CardPinNotice | null,
  key: string,
): CardPinNotice | null {
  return previous !== null && previous.key !== key ? previous : null;
}

/** Какой отказ называть полосой; остальные исходы полосы не ставят. */
export function cardPinOutcomeNoticeKind(
  outcome: "ok" | "invalid" | "unavailable" | "ignored" | "unauthorized",
): "invalid" | "unavailable" | null {
  return outcome === "invalid" || outcome === "unavailable" ? outcome : null;
}

export function cardPinButtonLabel(ticker: string, pinned: boolean): string {
  return pinned ? `Открепить карточку ${ticker}` : `Закрепить карточку ${ticker}`;
}

/** Тело пустой закреплённой карточки: «плотностей нет» или сколько скрыто фильтрами. */
export function cardEmptyText(hiddenCount: number): string {
  return hiddenCount === 0 ? "Плотностей нет" : `Скрыто фильтрами: ${hiddenCount}`;
}

/** Отказ записи закрепления: недоступность демона и отказ демона названы по-разному. */
export function cardPinNoticeText(ticker: string, outcome: "invalid" | "unavailable"): string {
  const reason = outcome === "invalid" ? "демон не принял запрос" : "демон недоступен";
  return `Закрепление карточки ${ticker}: не записалось — ${reason}`;
}

/**
 * Что делает нажатие на значок закрепления (по образцу `favoriteClickIntent`):
 * ключ в пути или список читается сейчас — игнорировать; список не прочитан — перечитать, а не писать
 * (клиент не знает, ставить или снимать); иначе — переключить.
 */
export type CardPinClickIntent =
  | { kind: "ignore" }
  | { kind: "reload" }
  | { kind: "toggle"; currentlyPinned: boolean };

export function cardPinClickIntent(
  status: "loading" | "ready" | "unavailable",
  pendingKeys: ReadonlySet<string>,
  pinnedKeys: ReadonlySet<string>,
  key: string,
): CardPinClickIntent {
  if (pendingKeys.has(key)) {
    return { kind: "ignore" };
  }
  if (status === "loading") {
    // Список перечитывается прямо сейчас: второй `GET` не нужен.
    return { kind: "ignore" };
  }
  if (status === "unavailable") {
    return { kind: "reload" };
  }
  return { kind: "toggle", currentlyPinned: pinnedKeys.has(key) };
}

/**
 * База биржи для тикера карточки по ключу инструмента: индекс потока, затем
 * база из записи закрепления, затем пустая — тогда `tickerLabel` откатывается
 * на приведённую (design Д9).
 */
export function cardExchangeBase(
  key: string,
  streamBases: ReadonlyMap<string, string>,
  pinBases: ReadonlyMap<string, string>,
): string {
  return streamBases.get(key) ?? pinBases.get(key) ?? "";
}
